import React, { useState, useEffect } from 'react';
import api from '../api';
import SectionActions from './SectionActions';
import PrintHeader from './PrintHeader';
import {
  Calendar, Plus, Trash2, Edit, Search, Clock, User, Phone,
  Building2, MessageCircle, AlertCircle, CheckCircle2
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';

const Appuntamenti = () => {
  const [appuntamenti, setAppuntamenti] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('prossimi');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    titolo: '',
    descrizione: '',
    data: '',
    ora_inizio: '',
    ora_fine: '',
    cliente: '',
    telefono: '',
    cantiereId: '',
    note: ''
  });

  const loadData = async () => {
    try {
      const [a, c] = await Promise.all([api.getAppuntamenti(), api.getCantieri()]);
      setAppuntamenti(a);
      setCantieri(c);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!showAdd) {
      resetForm();
    }
  }, [showAdd]);

  const today = new Date().toISOString().split('T')[0];

  const filtered = appuntamenti.filter(a => {
    const matchFilter = filter === 'tutti' || 
      (filter === 'prossimi' && a.data >= today) || 
      (filter === 'passati' && a.data < today);
    const matchSearch = !search || 
      a.titolo?.toLowerCase().includes(search.toLowerCase()) ||
      a.cliente?.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const resetForm = () => {
    setFormData({
      titolo: '',
      descrizione: '',
      data: '',
      ora_inizio: '',
      ora_fine: '',
      cliente: '',
      telefono: '',
      cantiereId: '',
      note: ''
    });
    setEditing(null);
    setShowAdd(false);
  };

  const handleSubmit = async () => {
    if (!formData.titolo || !formData.data) return;
    try {
      if (editing) {
        await api.updateAppuntamento(editing.id, formData);
      } else {
        await api.createAppuntamento(formData);
      }
      resetForm();
      loadData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleEdit = (a) => {
    setFormData({
      titolo: a.titolo || '',
      descrizione: a.descrizione || '',
      data: a.data || '',
      ora_inizio: a.ora_inizio || '',
      ora_fine: a.ora_fine || '',
      cliente: a.cliente || '',
      telefono: a.telefono || '',
      cantiereId: a.cantiereId || '',
      note: a.note || ''
    });
    setEditing(a);
    setShowAdd(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo appuntamento?')) return;
    try {
      await api.deleteAppuntamento(id);
      loadData();
    } catch (e) {
      console.error(e);
    }
  };

  const shareWhatsApp = (a) => {
    const cantiere = cantieri.find(c => c.id === a.cantiereId);
    const message = `*Promemoria Appuntamento*\n\n📅 *${a.titolo}*\n\nData: ${new Date(a.data + 'T12:00').toLocaleDateString('it-IT')}\n${a.ora_inizio ? `Ora: ${a.ora_inizio}${a.ora_fine ? ` - ${a.ora_fine}` : ''}` : ''}\n${a.cliente ? `Cliente: ${a.cliente}` : ''}\n${cantiere ? `Cantiere: ${cantiere.name}` : ''}\n${a.descrizione ? `\n${a.descrizione}` : ''}\n\n_Colabella Group_`;
    const phone = a.telefono ? a.telefono.replace(/\D/g, '') : '';
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const prossimi = appuntamenti.filter(a => a.data >= today);
  const oggi = appuntamenti.filter(a => a.data === today);

  if (loading) {
    return <div className="section-container"><p>Caricamento...</p></div>;
  }

  return (
    <div className="section-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <PrintHeader />
      <SectionActions title="Registro Appuntamenti" subtitle="Gestisci appuntamenti e calendari" />

      {/* Alerts */}
      {oggi.length > 0 && (
        <div className="fatt-alert" style={{ background: '#fef3c7', borderColor: '#fbbf24' }}>
          <AlertCircle size={20} color="#f59e0b" />
          <div>
            <strong>{oggi.length} appuntament{oggi.length === 1 ? 'o' : 'i'} oggi</strong>
            <span> — {oggi.map(a => a.titolo).join(', ')}</span>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="presenze-stats">
        <div className="pstat">
          <span className="pstat-value" style={{ color: '#3b82f6' }}>{prossimi.length}</span>
          <span className="pstat-label">Prossimi</span>
        </div>
        <div className="pstat">
          <span className="pstat-value" style={{ color: '#f59e0b' }}>{oggi.length}</span>
          <span className="pstat-label">Oggi</span>
        </div>
        <div className="pstat">
          <span className="pstat-value" style={{ color: '#6b7280' }}>{appuntamenti.length}</span>
          <span className="pstat-label">Totali</span>
        </div>
      </div>

      {/* Filters */}
      <div className="presenze-filters">
        <div className="search-bar" style={{ maxWidth: '300px' }}>
          <Search size={18} className="search-icon" />
          <Input placeholder="Cerca appuntamento..." value={search} onChange={e => setSearch(e.target.value)} className="search-input" />
        </div>
        <div className="cantiere-filter">
          {['prossimi', 'tutti', 'passati'].map(f => (
            <button key={f} className={`filter-chip ${filter === f ? 'filter-chip-active' : ''}`} onClick={() => setFilter(f)}>
              {f === 'prossimi' ? 'Prossimi' : f === 'tutti' ? 'Tutti' : 'Passati'}
            </button>
          ))}
        </div>
      </div>

      {/* Add Button */}
      <div className="mag-action-row">
        <button className="big-action-btn" onClick={() => { setShowAdd(true); setEditing(null); setFormData({ titolo: '', descrizione: '', data: '', ora_inizio: '', ora_fine: '', cliente: '', telefono: '', cantiereId: '', note: '' }); }}>
          <Plus size={20} /> Nuovo Appuntamento
        </button>
      </div>

      {/* List */}
      <div className="fatt-list">
        {filtered.map(a => {
          const isPast = a.data < today;
          const isToday = a.data === today;
          const cantiere = cantieri.find(c => c.id === a.cantiereId);
          return (
            <div key={a.id} className={`fatt-card ${isPast ? '' : isToday ? 'fatt-scaduta' : ''}`} style={{ borderLeftColor: isToday ? '#f59e0b' : isPast ? '#9ca3af' : '#3b82f6' }}>
              <div className="fatt-row-main" style={{ cursor: 'default' }}>
                <div className="fatt-left">
                  <Calendar size={20} color={isToday ? '#f59e0b' : isPast ? '#9ca3af' : '#3b82f6'} />
                  <div>
                    <h4 className="fatt-supplier">{a.titolo}</h4>
                    {a.descrizione && <p className="fatt-detail">{a.descrizione}</p>}
                    {a.cliente && <p className="fatt-detail"><User size={11} /> {a.cliente}</p>}
                    {cantiere && <p className="fatt-cantiere"><Building2 size={11} /> {cantiere.name}</p>}
                  </div>
                </div>
                <div className="fatt-center">
                  <span className="fatt-dates">
                    <Calendar size={11} /> {new Date(a.data + 'T12:00').toLocaleDateString('it-IT')}
                  </span>
                  {a.ora_inizio && (
                    <span className="fatt-detail" style={{ fontSize: 12, color: '#6b7280' }}>
                      <Clock size={11} /> {a.ora_inizio} {a.ora_fine && `- ${a.ora_fine}`}
                    </span>
                  )}
                </div>
                <div className="fatt-actions">
                  {a.telefono && (
                    <button 
                      className="doc-share-btn" 
                      style={{ background: '#25D366', color: 'white' }} 
                      onClick={() => shareWhatsApp(a)}
                      title="Condividi su WhatsApp"
                    >
                      <MessageCircle size={14} />
                    </button>
                  )}
                  <button className="mag-action-btn-sm" onClick={() => handleEdit(a)} title="Modifica">
                    <Edit size={13} />
                  </button>
                  <button className="pa-delete" onClick={() => handleDelete(a.id)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              {a.note && (
                <div style={{ padding: '8px 16px', fontSize: 12, color: '#6b7280', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                  <strong>Note:</strong> {a.note}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="empty-text">Nessun appuntamento. Crea il primo!</p>
        )}
      </div>

      {/* Add/Edit Dialog */}
      {showAdd && (
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifica Appuntamento' : 'Nuovo Appuntamento'}</DialogTitle>
            </DialogHeader>
            <div className="form-fields">
              <div className="form-field">
                <Label>Titolo *</Label>
                <Input value={formData.titolo} onChange={e => setFormData({...formData, titolo: e.target.value})} placeholder="Es: Sopralluogo cantiere" />
              </div>
              <div className="form-field">
                <Label>Descrizione</Label>
                <Textarea value={formData.descrizione} onChange={e => setFormData({...formData, descrizione: e.target.value})} placeholder="Dettagli appuntamento..." rows={2} />
              </div>
              <div className="form-row">
                <div className="form-field">
                  <Label>Data *</Label>
                  <Input type="date" value={formData.data} onChange={e => setFormData({...formData, data: e.target.value})} />
                </div>
                <div className="form-field">
                  <Label>Ora Inizio</Label>
                  <Input type="time" value={formData.ora_inizio} onChange={e => setFormData({...formData, ora_inizio: e.target.value})} />
                </div>
                <div className="form-field">
                  <Label>Ora Fine</Label>
                  <Input type="time" value={formData.ora_fine} onChange={e => setFormData({...formData, ora_fine: e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <Label>Cliente</Label>
                  <Input value={formData.cliente} onChange={e => setFormData({...formData, cliente: e.target.value})} placeholder="Nome cliente" />
                </div>
                <div className="form-field">
                  <Label>Telefono (per WhatsApp)</Label>
                  <Input value={formData.telefono} onChange={e => setFormData({...formData, telefono: e.target.value})} placeholder="+39 123 456 7890" />
                </div>
              </div>
              <div className="form-field">
                <Label>Cantiere (opzionale)</Label>
                <Select value={formData.cantiereId} onValueChange={v => setFormData({...formData, cantiereId: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleziona cantiere..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nessuno">Nessuno</SelectItem>
                    {cantieri.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="form-field">
                <Label>Note</Label>
                <Textarea value={formData.note} onChange={e => setFormData({...formData, note: e.target.value})} placeholder="Note aggiuntive..." rows={2} />
              </div>
              <button className="form-submit" onClick={handleSubmit} disabled={!formData.titolo || !formData.data}>
                {editing ? 'Salva Modifiche' : 'Crea Appuntamento'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Appuntamenti;
